import { formatDate, isStoreChargingTax } from "@/lib/utils";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShoppingCart,
  ArrowLeft,
  Package,
  Tag,
  Layers,
  AlertCircle,
  Info,
} from "lucide-react";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageContainer } from "@/components/layout/PageContainer";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { all_routes } from "../../Router/all_routes";

// ─── constants ───────────────────────────────────────────────────────────────
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

// ─── tax helper ──────────────────────────────────────────────────────────────
// Recalculates tax using current store settings so stale quotation rates are
// not carried over when the store's tax configuration has changed.
const recalcItemTax = (
  storeData,
  product,
  unitPrice,
  quantity,
  discountAmount,
) => {
  const baseAmount = round2(unitPrice * quantity - discountAmount);
  if (!isStoreChargingTax(storeData) || !product?.is_taxable) {
    return {
      tax_amount: 0,
      tax_percentage: 0,
      tax_class: null,
      line_total: baseAmount,
    };
  }
  const taxRate = parseFloat(storeData.tax_percentage) || 0;
  if ((product.tax_method || "exclusive") === "inclusive") {
    // Price already includes tax - nothing extra to add
    return {
      tax_amount: 0,
      tax_percentage: taxRate,
      tax_class: null,
      line_total: baseAmount,
    };
  }
  return {
    tax_amount: round2((baseAmount * taxRate) / 100),
    tax_percentage: taxRate,
    tax_class: null,
    line_total: baseAmount,
  };
};

const PAYMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
  { value: "cancelled", label: "Cancelled" },
  { value: "on-hold", label: "On Hold" },
  { value: "partially-refunded", label: "Partially Refunded" },
  { value: "partially-paid", label: "Partially Paid" },
];

// ─── main page ───────────────────────────────────────────────────────────────
const ConvertToSalePage = () => {
  const queryClient = useQueryClient();
  const { id: quotationId } = useParams();
  const navigate = useNavigate();
  const currentStore = useSelector((s) => s.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  useEffect(() => {
    if (!hasPermission("quotation convert") && !hasPermission("sale create")) {
      navigate(all_routes.dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── sale-level overrides ───────────────────────────────────────────────────
  const [saleInvDate, setSaleInvDate] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [notes, setNotes] = useState("");

  // ── expanded item rows ─────────────────────────────────────────────────────
  const [items, setItems] = useState([]);

  // ── fetch quotation details ────────────────────────────────────────────────
  const { data: quotationDetails, isLoading: loadingQuotation } = useQuery({
    queryKey: ["quotation-details-convert", quotationId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.QUOTATIONS.QUOTATION_DETAILS(quotationId),
        { params: { with: ["items", "items.product", "customer", "store"] } },
      );
      return res.data.data || res.data;
    },
    enabled: !!quotationId && hasPermission("quotation convert"),
    staleTime: 0,
  });

  // ── collect product IDs ────────────────────────────────────────────────────
  const productIds = useMemo(() => {
    const srcItems = quotationDetails?.items || [];
    return [
      ...new Set(srcItems.filter((i) => i.product_id).map((i) => i.product_id)),
    ];
  }, [quotationDetails]);

  // ── fetch each product individually by its ID ────────────────────────────
  const { data: posProducts = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["pos-products-for-conversion", productIds.join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        productIds.map((id) =>
          apiClient
            .get(API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(id))
            .then((res) => res.data.data || res.data)
            .catch(() => null),
        ),
      );
      return results.filter(Boolean);
    },
    enabled: productIds.length > 0 && hasPermission("product read"),
    staleTime: 30_000,
  });

  // ── id → product map ───────────────────────────────────────────────────────
  const productMap = useMemo(() => {
    const m = {};
    posProducts.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [posProducts]);

  // ── build expanded rows when data is ready ─────────────────────────────────
  useEffect(() => {
    const srcItems = quotationDetails?.items || [];
    if (!srcItems.length) return;
    if (productIds.length > 0 && loadingProducts) return;

    const rows = [];

    srcItems.forEach((item) => {
      const product = item.product_id ? productMap[item.product_id] : null;
      const isSerial =
        product?.tracking_type === "serial" || product?.is_serialized;
      const batches = product?.batches || [];
      const serials = product?.serial_numbers || [];

      if (isSerial) {
        const qty = Math.max(1, Math.round(parseFloat(item.quantity) || 1));
        const perUnitDiscount =
          qty > 1
            ? round2(parseFloat(item.discount_amount || 0) / qty)
            : parseFloat(item.discount_amount || 0);
        const perUnitDiscountValue =
          item.discount_type === "fixed" && qty > 1
            ? round2(parseFloat(item.discount_value || 0) / qty)
            : parseFloat(item.discount_value || 0);

        for (let i = 0; i < qty; i++) {
          rows.push({
            _key: `${item.id ?? item.quotation_item_id}-serial-${i}`,
            _isSerial: true,
            _isStandard: false,
            _isCustom: false,
            _product: product,
            _batches: batches,
            _serials: serials,
            quotation_item_id: item.id || item.quotation_item_id || null,
            product_id: item.product_id,
            is_new_item: false,
            item_name: item.item_name || product?.name || "",
            item_description: item.item_description || "",
            item_sku: item.item_sku || product?.sku || "",
            inventory_batch_id: null,
            inventory_serial_id: serials[i]?.id || null,
            quantity: 1,
            unit_price: parseFloat(item.unit_price || 0),
            original_price: parseFloat(
              item.original_price || item.unit_price || 0,
            ),
            discount_amount: perUnitDiscount,
            discount_type: item.discount_type || null,
            discount_value: perUnitDiscountValue,
            ...recalcItemTax(
              currentStore,
              product,
              parseFloat(item.unit_price || 0),
              1,
              perUnitDiscount,
            ),
            notes: item.notes || "",
            product_data: item.product_data || null,
          });
        }
      } else {
        const isCustom =
          item.is_new_item || (!item.product_id && !!item.item_name);
        const isStandard = !isCustom && product?.tracking_type === "standard";

        rows.push({
          _key: item.id || item.quotation_item_id || `row-${rows.length}`,
          _isSerial: false,
          _isStandard: isStandard,
          _isCustom: isCustom,
          _product: product,
          _batches: batches,
          _serials: [],
          quotation_item_id: item.id || item.quotation_item_id || null,
          product_id: item.product_id || null,
          is_new_item: item.is_new_item || false,
          item_name: item.item_name || product?.name || "",
          item_description: item.item_description || "",
          item_sku: item.item_sku || product?.sku || "",
          inventory_batch_id: batches.length > 0 ? batches[0].id : null,
          inventory_serial_id: null,
          quantity: parseFloat(item.quantity || 1),
          unit_price: parseFloat(item.unit_price || 0),
          original_price: parseFloat(
            item.original_price || item.unit_price || 0,
          ),
          discount_amount: parseFloat(item.discount_amount || 0),
          discount_type: item.discount_type || null,
          discount_value: parseFloat(item.discount_value || 0),
          ...(!isCustom && product
            ? recalcItemTax(
              currentStore,
              product,
              parseFloat(item.unit_price || 0),
              parseFloat(item.quantity || 1),
              parseFloat(item.discount_amount || 0),
            )
            : {
              tax_amount: parseFloat(item.tax_amount || 0),
              tax_percentage: parseFloat(item.tax_percentage || 0),
              tax_class: item.tax_class || null,
              line_total: parseFloat(item.line_total || 0),
            }),
          notes: item.notes || "",
          product_data: item.product_data || null,
        });
      }
    });

    setItems(rows);
  }, [quotationDetails, productMap, productIds, loadingProducts, currentStore]);

  // ── helpers ────────────────────────────────────────────────────────────────
  const takenSerialIds = (currentIndex) =>
    items
      .filter(
        (it, i) => i !== currentIndex && it._isSerial && it.inventory_serial_id,
      )
      .map((it) => it.inventory_serial_id);

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // ── totals ─────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const subtotal = items.reduce(
      (s, it) =>
        s + parseFloat(it.unit_price || 0) * parseFloat(it.quantity || 1),
      0,
    );
    const taxTotal = items.reduce(
      (s, it) => s + parseFloat(it.tax_amount || 0),
      0,
    );
    const discountTotal = items.reduce(
      (s, it) => s + parseFloat(it.discount_amount || 0),
      0,
    );
    const lineTotal = items.reduce(
      (s, it) => s + parseFloat(it.line_total || 0),
      0,
    );
    return {
      subtotal: round2(subtotal),
      taxTotal: round2(taxTotal),
      discountTotal: round2(discountTotal),
      grandTotal: round2(lineTotal + taxTotal),
    };
  }, [items]);

  // ── submit ─────────────────────────────────────────────────────────────────
  const convertMutation = useMutation({
    mutationFn: async () => {
      const payloadItems = items.map((item) => ({
        quotation_item_id: item.quotation_item_id || null,
        product_id: item.product_id || null,
        is_new_item: item.is_new_item || false,
        item_name: item.item_name || null,
        item_description: item.item_description || null,
        item_sku: item.item_sku || null,
        inventory_batch_id: item.inventory_batch_id || null,
        inventory_serial_id: item.inventory_serial_id || null,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        original_price: parseFloat(item.original_price || item.unit_price || 0),
        discount_amount: parseFloat(item.discount_amount || 0),
        discount_type: item.discount_type || null,
        discount_value: parseFloat(item.discount_value || 0),
        tax_amount: parseFloat(item.tax_amount || 0),
        tax_class: item.tax_class || null,
        tax_percentage: parseFloat(item.tax_percentage || 0),
        line_total: parseFloat(item.line_total || 0),
        notes: item.notes || null,
        product_data: item.product_data || null,
      }));

      const res = await apiClient.post(
        API_ENDPOINTS.QUOTATIONS.CONVERT_TO_SALE(quotationId),
        {
          sale_inv_date: saleInvDate || null,
          payment_status: paymentStatus,
          notes: notes || null,
          items: payloadItems,
        },
      );
      return res.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["quotations"],
        refetchType: "all",
      });
      await queryClient.invalidateQueries({
        queryKey: ["sales"],
        refetchType: "all",
      });
      toast.success("Quotation converted to sale successfully");
      navigate(all_routes.saleslist);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to convert quotation to sale",
      );
    },
  });

  const handleSubmit = () => {
    const missingSerial = items.find(
      (it) =>
        it._product?.manage_inventory &&
        it._isSerial &&
        !it.inventory_serial_id,
    );
    if (missingSerial) {
      toast.error(
        `Please select a serial number for all serial product rows (${missingSerial.item_name})`,
      );
      return;
    }

    const missingBatch = items.find(
      (it) =>
        it._product?.manage_inventory &&
        it._isStandard &&
        !it.inventory_batch_id,
    );
    if (missingBatch) {
      toast.error(`Please select a batch for "${missingBatch.item_name}"`);
      return;
    }

    convertMutation.mutate();
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const isLoading =
    loadingQuotation || (productIds.length > 0 && loadingProducts);
  const quotationRef =
    quotationDetails?.quotation_number ||
    (quotationId ? `QT-${quotationId.slice(0, 8)}` : "");

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Convert to Sale"
          subtitle={quotationRef ? `From quotation ${quotationRef}` : ""}
        />
        <PageHeaderActions>
          <Button variant="outline" asChild>
            <Link to={all_routes.quotationsList}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Quotations
            </Link>
          </Button>
        </PageHeaderActions>
      </PageHeader>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner className="h-8 w-8" />
          <span className="ml-3 text-muted-foreground">Loading quotation…</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {/* ── Sale Info ───────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Info className="h-4 w-4 text-primary" />
                Sale Override Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Quotation summary */}
                {quotationDetails && (
                  <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/40 rounded-lg text-sm">
                    <div>
                      <span className="text-muted-foreground block">
                        Customer
                      </span>
                      <span className="font-medium">
                        {quotationDetails.customer?.name ||
                          quotationDetails.customer?.full_name ||
                          "-"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">Store</span>
                      <span className="font-medium">
                        {quotationDetails.store?.name || "-"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">
                        Quotation Date
                      </span>
                      <span className="font-medium">
                        {quotationDetails.quotation_date
                          ? formatDate(quotationDetails.quotation_date,)
                          : "-"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">
                        Quoted Total
                      </span>
                      <span className="font-medium">
                        {currencySymbol}
                        {parseFloat(quotationDetails.total || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="sale-inv-date">Sale Invoice Date</Label>
                  <Input
                    id="sale-inv-date"
                    type="datetime-local"
                    value={saleInvDate}
                    onChange={(e) => setSaleInvDate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use current date/time
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-status">
                    Payment Status <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={paymentStatus}
                    onValueChange={setPaymentStatus}
                  >
                    <SelectTrigger id="payment-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="conversion-notes">Notes</Label>
                  <Textarea
                    id="conversion-notes"
                    rows={3}
                    placeholder="Optional sale notes…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Items ───────────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4 text-primary" />
                Items
                <span className="ml-1 text-sm font-normal text-muted-foreground">
                  {items.length} row{items.length !== 1 ? "s" : ""}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center border rounded-md">
                  <AlertCircle className="h-4 w-4" />
                  No items found on this quotation
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Item</TableHead>
                        <TableHead className="w-20 text-center">Qty</TableHead>
                        <TableHead className="w-32 text-right">
                          Unit Price
                        </TableHead>
                        <TableHead className="w-32 text-right">
                          Discount
                        </TableHead>
                        <TableHead className="w-28 text-right">Tax</TableHead>
                        <TableHead className="min-w-[220px]">
                          Batch / Serial
                        </TableHead>
                        <TableHead className="w-32 text-right">
                          Line Total
                        </TableHead>
                        <TableHead className="min-w-[180px]">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, idx) => (
                        <ItemRow
                          key={item._key}
                          item={item}
                          index={idx}
                          currencySymbol={currencySymbol}
                          takenSerialIds={takenSerialIds(idx)}
                          onUpdate={updateItem}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Totals + Actions ────────────────────────────────────────── */}
          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-end">
            {/* Totals */}
            <Card className="w-full md:w-80">
              <CardContent className="pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>
                    {currencySymbol}
                    {totals.subtotal.toFixed(2)}
                  </span>
                </div>
                {totals.discountTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-destructive">
                      -{currencySymbol}
                      {totals.discountTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {totals.taxTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span>
                      {currencySymbol}
                      {totals.taxTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-base border-t pt-2 mt-1">
                  <span>Total</span>
                  <span>
                    {currencySymbol}
                    {totals.grandTotal.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(all_routes.quotationsList)}
                disabled={convertMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="lg"
                onClick={handleSubmit}
                disabled={
                  isLoading ||
                  convertMutation.isPending ||
                  items.length === 0 ||
                  (!hasPermission("quotation convert") &&
                    !hasPermission("sale create"))
                }
              >
                {convertMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Converting…
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Convert to Sale
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

// ─── ItemRow ──────────────────────────────────────────────────────────────────
const ItemRow = ({ item, index, currencySymbol, takenSerialIds, onUpdate }) => {
  const serialOptions = (item._serials || []).filter(
    (s) => !takenSerialIds.includes(s.id) || s.id === item.inventory_serial_id,
  );

  return (
    <TableRow className="align-top">
      {/* Item name */}
      <TableCell>
        <div className="font-medium leading-tight">{item.item_name || "-"}</div>
        {item.item_sku && (
          <div className="text-xs text-muted-foreground mt-0.5">
            SKU: {item.item_sku}
          </div>
        )}
        {item.item_description && (
          <div className="text-xs text-muted-foreground mt-0.5 max-w-[200px] truncate">
            {item.item_description}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-1.5">
          {item._isSerial && (
            <Badge variant="secondary" className="text-[10px] py-0 h-4">
              <Tag className="h-2.5 w-2.5 mr-0.5" />
              Serial
            </Badge>
          )}
          {item._isStandard && (
            <Badge variant="outline" className="text-[10px] py-0 h-4">
              <Layers className="h-2.5 w-2.5 mr-0.5" />
              Batch
            </Badge>
          )}
          {item._isCustom && (
            <Badge className="text-[10px] py-0 h-4 bg-amber-100 text-amber-800 border-amber-300">
              Custom
            </Badge>
          )}
        </div>
      </TableCell>

      {/* Qty */}
      <TableCell className="text-center pt-3">
        {item._isSerial ? (
          <span className="text-sm text-muted-foreground">1</span>
        ) : (
          <Input
            type="number"
            min="0.0001"
            step="1"
            className="w-20 text-center h-8 px-2"
            value={item.quantity}
            onChange={(e) =>
              onUpdate(index, "quantity", parseFloat(e.target.value) || 1)
            }
          />
        )}
      </TableCell>

      {/* Unit Price */}
      <TableCell className="text-right pt-3">
        <Input
          type="number"
          min="0"
          step="0.01"
          className="w-28 text-right h-8 px-2"
          value={item.unit_price}
          onChange={(e) =>
            onUpdate(index, "unit_price", parseFloat(e.target.value) || 0)
          }
        />
      </TableCell>

      {/* Discount */}
      <TableCell className="text-right pt-3">
        <span className="text-sm">
          {item.discount_amount > 0 ? (
            <>
              -{currencySymbol}
              {parseFloat(item.discount_amount).toFixed(2)}
              {item.discount_type === "percentage" &&
                item.discount_value > 0 && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({item.discount_value}%)
                  </span>
                )}
            </>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </span>
      </TableCell>

      {/* Tax */}
      <TableCell className="text-right pt-3">
        <span className="text-sm">
          {item.tax_amount > 0 ? (
            <>
              {currencySymbol}
              {parseFloat(item.tax_amount).toFixed(2)}
            </>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </span>
      </TableCell>

      {/* Batch / Serial */}
      <TableCell className="pt-3">
        {item._isSerial ? (
          item._serials?.length > 0 ? (
            <Select
              value={item.inventory_serial_id ?? "__none__"}
              onValueChange={(v) =>
                onUpdate(
                  index,
                  "inventory_serial_id",
                  v === "__none__" ? null : v,
                )
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select serial…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">- none -</SelectItem>
                {serialOptions.map((sn) => (
                  <SelectItem key={sn.id} value={sn.id}>
                    {sn.serial_number}
                    {sn.status && sn.status !== "available" && (
                      <span className="ml-1 text-muted-foreground">
                        ({sn.status})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-xs text-muted-foreground">
              No serials available
            </span>
          )
        ) : item._isStandard ? (
          item._batches?.length > 0 ? (
            <Select
              value={item.inventory_batch_id ?? "__none__"}
              onValueChange={(v) =>
                onUpdate(
                  index,
                  "inventory_batch_id",
                  v === "__none__" ? null : v,
                )
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select batch…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">- none -</SelectItem>
                {item._batches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.batch_number || b.id.slice(0, 8)}
                    {b.quantity_available != null && (
                      <span className="ml-1 text-muted-foreground">
                        (qty: {b.quantity_available})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-xs text-muted-foreground">No batches</span>
          )
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Line Total */}
      <TableCell className="text-right pt-3 font-medium text-sm">
        {currencySymbol}
        {parseFloat(item.line_total || 0).toFixed(2)}
      </TableCell>

      {/* Notes */}
      <TableCell className="pt-3">
        <Input
          type="text"
          placeholder="Notes…"
          className="h-8 text-xs"
          value={item.notes || ""}
          onChange={(e) => onUpdate(index, "notes", e.target.value)}
        />
      </TableCell>
    </TableRow>
  );
};

export default ConvertToSalePage;
